import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarOff, Sparkles, Briefcase, Clock4, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtNum = (n) => (Number(n) || 0).toFixed(1).replace(/\.0$/, "");

const currentFY = () => {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${y + 1}`;
};

export default function LeaveBalances() {
  const [fy, setFy] = useState(currentFY());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leave-balances?fy=${fy}`);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const yearEnd = async () => {
    if (!window.confirm(`Run year-end processing for FY ${fy}? This carries 50% of unused EL to the next year and encashes the rest of EL/SL/CL using each employee's March salary ÷ 30 as the daily rate. This cannot be undone via the UI.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leave-balances/year-end-process?fy=${fy}`);
      toast.success(`Year-end done: ${data.processed} processed · ${data.skipped} skipped · total ${fmtINR(data.total_encashed)} encashed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const totals = rows.reduce((t, r) => {
    t.el += r.el_remaining || 0;
    t.sl += r.sl_remaining || 0;
    t.cl += r.cl_remaining || 0;
    t.encash += r.total_encashed_amount || 0;
    return t;
  }, { el: 0, sl: 0, cl: 0, encash: 0 });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Time off</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Leave Balances</h1>
          <p className="text-sm text-zinc-500 mt-2">Per-employee EL / SL / CL balances. Accrual is lazy: opens auto-compute the latest state on every load.</p>
        </div>
        <div className="flex gap-2 items-center">
          <input type="text" value={fy} onChange={(e) => setFy(e.target.value)} placeholder="YYYY-YYYY" className="border border-zinc-200 rounded px-2 py-1 text-sm w-32" data-testid="fy-input" />
          <Button onClick={yearEnd} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="year-end-button">
            <FileCheck2 className="w-4 h-4 mr-1" /> Run year-end for FY {fy}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Sparkles} label="Total EL remaining" value={`${fmtNum(totals.el)} days`} />
        <Stat icon={CalendarOff} label="Total SL remaining" value={`${fmtNum(totals.sl)} days`} />
        <Stat icon={Briefcase} label="Total CL remaining" value={`${fmtNum(totals.cl)} days`} />
        <Stat icon={FileCheck2} label="Encashed (finalized)" value={fmtINR(totals.encash)} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-3">Employee</th>
              <th className="py-3 px-3 text-right">EL accrued</th>
              <th className="py-3 px-3 text-right">EL used</th>
              <th className="py-3 px-3 text-right">EL remain</th>
              <th className="py-3 px-3 text-right">SL acc / used / rem</th>
              <th className="py-3 px-3 text-right">CL acc / used / rem</th>
              <th className="py-3 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id || r.user_id} className="border-b border-zinc-100" data-testid={`lb-row-${r.user_id}`}>
                <td className="py-2.5 px-3">
                  <div className="font-medium">{r.user_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.employee_id || "—"}</div>
                </td>
                <td className="py-2.5 px-3 text-right font-mono">{fmtNum(r.el_accrued)}{r.el_carry_forward ? <span className="text-[10px] text-emerald-700"> +{fmtNum(r.el_carry_forward)}cf</span> : null}</td>
                <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtNum(r.used?.el)}</td>
                <td className="py-2.5 px-3 text-right font-mono font-semibold">{fmtNum(r.el_remaining)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-xs">{fmtNum(r.sl_accrued)} / {fmtNum(r.used?.sl)} / <span className="font-bold">{fmtNum(r.sl_remaining)}</span></td>
                <td className="py-2.5 px-3 text-right font-mono text-xs">{fmtNum(r.cl_accrued)} / {fmtNum(r.used?.cl)} / <span className="font-bold">{fmtNum(r.cl_remaining)}</span></td>
                <td className="py-2.5 px-3">
                  {r.finalized ? (
                    <span className="text-[10px] uppercase tracking-widest font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded" title={`Encashed ${fmtINR(r.total_encashed_amount)}`}>Finalized · {fmtINR(r.total_encashed_amount)}</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Active</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={7} className="py-10 text-center text-zinc-500">No employees in scope.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-zinc-500" /><span className="label-overline">{label}</span></div>
      <div className="text-2xl font-bold font-display mt-1 font-mono">{value}</div>
    </div>
  );
}
